import { getLocalTimeZone, parseAbsolute } from '@internationalized/date';
import { createParser, parseAsString } from 'nuqs';
import { isEmptyString } from '@/utils/string';
import type { ActivityLogFilters } from './types';

/**
 * The Activity Type filter is contextual: the paired `name` filter (event name / attribute name /
 * free-text) is only meaningful for the currently selected type. So when the type changes, clear
 * `name` — otherwise a carried-over name silently filters the list down to nothing.
 */
export function filtersForTypeChange(type: string): Pick<ActivityLogFilters, 'type' | 'name'> {
  return { type, name: '' };
}

/** Whether a `from`/`to` value is an absolute date-time the logs endpoint can read. */
export function isLogDate(value: string): boolean {
  try {
    parseAbsolute(value, getLocalTimeZone());
    return true;
  } catch {
    return false;
  }
}

/**
 * A `from`/`to` search param. Anything that is not a date-time reads as absent, so it is never sent
 * to the logs endpoint: a stray `?from=campaigns` (a nav param the back button carries over) would
 * otherwise filter the feed by garbage. Ember's logs controller guards the same way (FPS-2181).
 */
const parseAsLogDate = createParser({
  parse: (value: string) => (isLogDate(value) ? value : null),
  serialize: (value: string) => value,
});

/** The feed's filters as URL state, one search param each. */
export const activityLogFilterParsers = {
  type: parseAsString.withDefault(''),
  name: parseAsString.withDefault(''),
  from: parseAsLogDate.withDefault(''),
  to: parseAsLogDate.withDefault(''),
};

/**
 * Whether any filter narrows the feed. A date range counts only once both of its bounds are set
 * (Ember `activity-logs/list` `hasAnyFilters`): a lone bound leaves the feed showing everything.
 */
export function isFeedFiltered({ type, name, from, to }: ActivityLogFilters): boolean {
  return !isEmptyString(type) || !isEmptyString(name) || (!isEmptyString(from) && !isEmptyString(to));
}
